const ARTEL_TYPE = 'артельщик'
const HIRED_TYPE = 'наемный'
const IP_TYPE = 'ип'

function num(value, fallback = 0) {
  if (value === null || value === undefined || value === '') return fallback
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback
  const parsed = Number(String(value).replace(/\s/g, '').replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : fallback
}

function roundRub(value) {
  return Math.round(num(value))
}

function normalizeType(type) {
  return String(type || '').trim().toLowerCase()
}

export function normalizeSharePercent(value) {
  return num(value)
}

export function isArtel(type) {
  return normalizeType(type) === ARTEL_TYPE
}

export function isHired(type) {
  return normalizeType(type) === HIRED_TYPE
}

export function isIp(type) {
  return normalizeType(type) === IP_TYPE
}

export function createDefaultIncomeReportConfig(seed = {}) {
  return {
    labor_pay_per_person: 0,
    fund_start: 0,
    income_acts: 0,
    tax_fact: 0,
    tax_plan: 0,
    other_exp_fact: 0,
    other_exp_plan: 0,
    profit_distribution: null,
    hired_salaries: [],
    additional_employees: [],
    expenses: [],
    labor_exceptions: [],
    ...seed,
    hired_salaries: seed.hired_salaries || [],
    additional_employees: seed.additional_employees || [],
    expenses: seed.expenses || [],
    labor_exceptions: seed.labor_exceptions || [],
  }
}

function rowKey(row, index) {
  return String(row?.row_key || row?.employee_id || row?.id || `row:${index}`)
}

function indexByRowKey(items = []) {
  const map = new Map()
  items.forEach((item) => {
    const key = String(item?.row_key || item?.employee_id || '')
    const field = String(item?.field || '')
    if (!key || !field) return
    if (!map.has(key)) map.set(key, {})
    map.get(key)[field] = num(item?.value)
  })
  return map
}

function indexMoneyByEmployee(items = [], field) {
  const map = new Map()
  items.forEach((item) => {
    const key = String(item?.employee_id || item?.row_key || '')
    if (!key) return
    map.set(key, num(item?.[field]))
  })
  return map
}

function expenseTotals(expenses = [], category) {
  return expenses
    .filter((item) => String(item?.category || '') === category)
    .reduce((acc, item) => {
      const type = String(item?.type || '')
      if (type === 'income') acc.income += num(item?.amount)
      if (type === 'expense') acc.expense += num(item?.amount)
      return acc
    }, { income: 0, expense: 0 })
}

const TOTAL_FIELDS = [
  'sharePercent',
  'otherParticipation',
  'actsSum',
  'toReceive',
  'delta',
  'salary',
  'laborPay',
  'sharePayment',
  'maxOther',
  'realOther',
  'cash',
  'cashRounded',
  'otherRounded',
  'cashPotential',
  'cashPayment',
  'otherPayment',
  'cashDistribution',
  'cashDeskSupplement',
  'bank',
  'totalBeforeTax',
  'totalAfterTax',
]

function buildTotals(rows = []) {
  return TOTAL_FIELDS.reduce((acc, field) => {
    acc[field] = rows.reduce((sum, row) => sum + (row[field] || 0), 0)
    return acc
  }, {})
}

export function calculateIncomeReport({ sourceRows = [], config: rawConfig = {}, overrides = [] } = {}) {
  const config = createDefaultIncomeReportConfig(rawConfig)
  const overrideMap = indexByRowKey(overrides)
  const salaryMap = indexMoneyByEmployee(config.hired_salaries, 'salary')
  const laborExceptionMap = indexMoneyByEmployee(config.labor_exceptions, 'amount')
  const additionalTotals = expenseTotals(config.expenses, 'additional')
  const mainTotals = expenseTotals(config.expenses, 'main')
  const additionalBalance = additionalTotals.income - additionalTotals.expense
  const miscExpenseAmount = Math.max(0, roundRub(additionalTotals.expense - additionalTotals.income))

  const baseRows = sourceRows.map((sourceRow, index) => {
    const key = rowKey(sourceRow, index)
    const employeeId = String(sourceRow.employee_id || key)
    const override = overrideMap.get(key) || overrideMap.get(employeeId) || {}
    const type = sourceRow.employee_type || '—'
    const toReceiveDefault = num(sourceRow.to_receive_default, num(sourceRow.acts_sum))
    const sharePercentDefault = normalizeSharePercent(sourceRow.share_percent)
    const sharePercent = override.share_percent ?? sharePercentDefault
    const salaryDefault = isHired(type) ? salaryMap.get(employeeId) ?? salaryMap.get(key) ?? 0 : 0
    const salary = isHired(type) ? override.salary ?? salaryDefault : 0
    const toReceive = override.to_receive ?? toReceiveDefault
    const actsSum = num(sourceRow.acts_sum)

    return {
      row_key: key,
      employee_id: sourceRow.employee_id || null,
      employee_name: sourceRow.employee_name || '—',
      employee_type: type || '—',
      team_name: sourceRow.team_name || 'Без команды',
      department_name: sourceRow.department_name || 'Без подразделения',
      sharePercent,
      sharePercentDefault,
      actsSum,
      toReceiveDefault,
      toReceive,
      delta: roundRub(actsSum - toReceive),
      salary,
      salaryDefault,
      isAdditional: false,
    }
  })

  const additionalRows = (config.additional_employees || []).map((item, index) => {
    const key = `additional:${item.id || index}`
    const override = overrideMap.get(key) || {}
    const type = item.employee_type || item.type || 'подрядчик'
    const payment = num(item.payment)
    const salaryDefault = isHired(type) ? num(item.salary) : 0
    const salary = isHired(type) ? override.salary ?? salaryDefault : 0
    return {
      row_key: key,
      employee_id: null,
      employee_name: item.name || '(без имени)',
      employee_type: type,
      team_name: '',
      department_name: '',
      sharePercent: 0,
      sharePercentDefault: 0,
      actsSum: 0,
      toReceiveDefault: payment,
      toReceive: override.to_receive ?? payment,
      delta: roundRub(0 - (override.to_receive ?? payment)),
      salary,
      salaryDefault,
      isAdditional: true,
    }
  })

  const preparedRows = [...baseRows, ...additionalRows]
  const participatingRows = preparedRows.filter((row) => isArtel(row.employee_type) || isHired(row.employee_type))
  const sumReceiveParticipating = participatingRows.reduce((sum, row) => sum + num(row.toReceive), 0)
  const sumHiredSalary = participatingRows
    .filter((row) => isHired(row.employee_type))
    .reduce((sum, row) => sum + num(row.salary), 0)
  const payFund = roundRub((sumReceiveParticipating + miscExpenseAmount - sumHiredSalary) / 2)

  const rows = preparedRows.map((row) => {
    const override = overrideMap.get(row.row_key) || overrideMap.get(String(row.employee_id || '')) || {}
    const result = { ...row }

    if (isArtel(row.employee_type)) {
      const laborException = laborExceptionMap.get(String(row.employee_id || '')) ?? laborExceptionMap.get(row.row_key)
      result.laborPay = laborException ?? num(config.labor_pay_per_person)
      result.sharePayment = roundRub(payFund * (row.sharePercent / 100))
      result.maxOther = roundRub(result.sharePayment * 1.2)
      const rawOther = roundRub(result.toReceive - result.laborPay - result.sharePayment)
      if (rawOther < 0) {
        result.realOther = 0
        result.cash = rawOther
      } else if (rawOther > result.maxOther) {
        result.realOther = result.maxOther
        result.cash = rawOther - result.maxOther
      } else {
        result.realOther = rawOther
        result.cash = 0
      }
      const maxAllowedCash = result.toReceive - result.laborPay - result.sharePayment
      let defaultCashRounded = Math.round(result.cash / 3000) * 3000
      if (defaultCashRounded > maxAllowedCash) {
        defaultCashRounded = Math.floor(maxAllowedCash / 3000) * 3000
      }
      result.cashRoundedDefault = defaultCashRounded
      result.cashRounded = override.cash_rounded ?? defaultCashRounded
      if (result.cashRounded > maxAllowedCash) {
        result.cashRounded = Math.floor(maxAllowedCash / 3000) * 3000
      }
      result.otherRounded = Math.max(0, roundRub(result.toReceive - result.laborPay - result.sharePayment - result.cashRounded))
      result.cashPotential = Math.max(0, result.maxOther - result.otherRounded - Math.abs(result.cashRounded))
    } else {
      result.laborPay = null
      result.sharePayment = null
      result.maxOther = null
      result.realOther = null
      result.cash = roundRub(result.toReceive - result.salary)
      result.cashRoundedDefault = result.cash
      result.cashRounded = result.cash
      result.otherRounded = null
      result.cashPotential = null
    }

    result.cashPayment = override.cash_payment ?? 0
    result.otherPayment = isArtel(row.employee_type)
      ? (result.otherRounded || 0) + result.cashPayment
      : 0
    result.bank = result.cashRounded - result.cashPayment
    result.cashDistribution = result.bank < 0 ? Math.abs(result.bank) : 0
    result.cashDeskSupplement = result.bank > 0 ? result.bank : 0
    result.totalBeforeTax = (result.laborPay || 0) + (result.sharePayment || 0) + result.otherPayment
    result.totalAfterTax = override.total_after_tax ?? (isArtel(row.employee_type) && config.ndfl_totals?.[row.employee_id] != null
      ? roundRub(config.ndfl_totals[row.employee_id])
      : roundRub(result.totalBeforeTax * 1.13))
    return result
  })

  const totalOtherPayment = rows.reduce((sum, row) => sum + (row.otherPayment || 0), 0)
  rows.forEach((row) => {
    row.otherParticipation = isArtel(row.employee_type) && totalOtherPayment > 0
      ? row.otherPayment / totalOtherPayment
      : null
  })

  const cashRoundedTotal = rows.reduce((sum, row) => sum + (row.cashRounded || 0), 0)
  const cashPaid = rows.reduce((sum, row) => sum + (row.cashPayment || 0), 0)
  const totalCashNeeded = cashRoundedTotal - additionalBalance
  const cashDelta = totalCashNeeded - cashPaid
  const employeeTotals = buildTotals(rows)
  const miscExpenseRow = {
    row_key: 'misc-expenses',
    employee_id: null,
    employee_name: 'Прочие расходы',
    employee_type: '',
    team_name: '',
    department_name: '',
    sharePercent: null,
    otherParticipation: null,
    actsSum: 0,
    toReceiveDefault: miscExpenseAmount,
    toReceive: miscExpenseAmount,
    delta: miscExpenseAmount > 0 ? -miscExpenseAmount : 0,
    salary: 0,
    laborPay: null,
    sharePayment: null,
    maxOther: null,
    realOther: null,
    cash: null,
    cashRoundedDefault: null,
    cashRounded: null,
    otherRounded: null,
    cashPotential: null,
    cashPayment: null,
    otherPayment: null,
    cashDistribution: null,
    cashDeskSupplement: null,
    bank: null,
    totalBeforeTax: null,
    totalAfterTax: null,
    isMiscExpense: true,
  }
  const grandTotals = buildTotals([...rows, miscExpenseRow])
  const paymentSummary = config.payment_summary || {}
  const cashActPayouts = num(paymentSummary.cash_payouts)
  const artelExpense = num(paymentSummary.artel_expense)
  const artelPayouts = num(paymentSummary.artel_payouts)
  const totalIncome = num(config.income_acts) + mainTotals.income
  const totalExpensesFact = num(config.tax_fact) + num(config.other_exp_fact) + mainTotals.expense + artelExpense
  const totalExpensesPlan = num(config.tax_plan) + num(config.other_exp_plan)
  const profit = totalIncome - totalExpensesFact
  const profitDistribution = config.profit_distribution === null || config.profit_distribution === undefined || config.profit_distribution === ''
    ? grandTotals.toReceive - cashActPayouts - artelPayouts
    : roundRub(config.profit_distribution)

  return {
    rows,
    miscExpenseRow,
    payFund,
    totals: employeeTotals,
    grandTotals,
    infoCards: {
      payFund,
      cashRoundedTotal,
      additionalBalance,
      totalCashNeeded,
      cashPaid,
      cashDelta,
      cashCovered: cashPaid >= totalCashNeeded,
    },
    paymentSummary,
    pnl: {
      artelExpense,
      cashActPayouts,
      fundStart: num(config.fund_start),
      totalIncome,
      endBalance: num(config.fund_start) + totalIncome,
      totalExpensesFact,
      totalExpensesPlan,
      totalExpensesDiff: totalExpensesPlan - totalExpensesFact,
      taxDiff: num(config.tax_plan) - num(config.tax_fact),
      otherExpenseDiff: num(config.other_exp_plan) - num(config.other_exp_fact),
      profit,
      profitDistribution,
      fundEnd: num(config.fund_start) + profit - profitDistribution,
    },
  }
}
